import React, { useState, useRef, useCallback } from 'react';
import DropArea from './DropArea';
import Results from './Results';
import Map from './Map';
import { parseGPX } from '../utils/gpxParser';
import { countLaps, separateCircularPaths, removeDuplicatePoints } from '../utils/lapDetector';

const GPXLapCounter = () => {
    const [track, setTrack] = useState(null);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [circularPaths, setCircularPaths] = useState(null);
    const [iqrMultiplier, setIqrMultiplier] = useState(1.5);
    const [recalculating, setRecalculating] = useState(false);
    const recalculateTimeoutRef = useRef(null);

    const handleFileChange = (selectedFile) => {
        // Clear previous states
        setError(null);
        setResults(null);
        setTrack(null);
        setCircularPaths(null);
        setLoading(true);

        // Validate file input
        if (!selectedFile || !(selectedFile instanceof File)) {
            setError('No file selected');
            setLoading(false);
            return;
        }

        if (!selectedFile.name.toLowerCase().endsWith('.gpx')) {
            setError('Please select a valid GPX file');
            setLoading(false);
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const allPoints = removeDuplicatePoints(parseGPX(e.target.result));
                const { circularPaths } = separateCircularPaths(allPoints);
                const data = countLaps(circularPaths, 10, { iqrMultiplier });
                setTrack(allPoints);
                setCircularPaths(circularPaths);
                // Include total track points so Results can display it
                setResults({ ...data, trackPoints: allPoints.length });
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        reader.onerror = () => {
            setError('Error reading file');
            setLoading(false);
        };

        reader.readAsText(selectedFile);
    };

    const handleOutlierSensitivityChange = useCallback((e) => {
        const newMultiplier = parseFloat(e.target.value);
        setIqrMultiplier(newMultiplier);
        
        // Clear any pending recalculation
        if (recalculateTimeoutRef.current) {
            clearTimeout(recalculateTimeoutRef.current);
        }
        
        // Debounce recalculation to avoid blocking UI during slider drag
        setRecalculating(true);
        recalculateTimeoutRef.current = setTimeout(() => {
            if (circularPaths && track) {
                const data = countLaps(circularPaths, 10, { iqrMultiplier: newMultiplier });
                setResults(prev => ({ ...prev, ...data, trackPoints: track.length }));
            }
            setRecalculating(false);
        }, 300); // Wait 300ms after slider stops moving
    }, [circularPaths, track]);

    return (
        <div className="gpx-lap-counter">
            <h1>GPX Lap Counter</h1>
            <DropArea onFileChange={handleFileChange} />
            {loading && <div className="loading">Analyzing GPX file...</div>}
            {error && <div className="error">{error}</div>}
            {results && (
                <details className="controls-section-collapsible">
                    <summary>Advanced Options</summary>
                    <div className="controls-section">
                        <label htmlFor="outlier-sensitivity">
                            Outlier Sensitivity (less aggressive →):
                            <input
                                id="outlier-sensitivity"
                                type="range"
                                min="0.5"
                                max="3"
                                step="0.1"
                                value={iqrMultiplier}
                                onChange={handleOutlierSensitivityChange}
                                disabled={recalculating}
                            />
                            <span className="multiplier-value">{iqrMultiplier.toFixed(1)}x</span>
                            {recalculating && <span className="recalculating">●</span>}
                        </label>
                        <small>Lower values filter more outliers, higher values keep more laps</small>
                    </div>
                </details>
            )}
            <div className="maps-container">
                {track && circularPaths && results && (
                    <div className="map-section">
                        <h2>Entire Path</h2>
                        <Map track={track} lapPoints={[]} />
                        <h2>Laps</h2>
                        <Map track={circularPaths} lapPoints={results.lapPoints} />
                        <Results results={results} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default GPXLapCounter;